import { useId } from "react";
import { AlertCircle } from "lucide-react";

/**
 * Text input / textarea with inline validation feedback.
 *
 * Errors are shown only after a field has been touched or a submit has been attempted, so
 * nobody is told they are wrong before they have typed anything. The message is wired via
 * aria-describedby and aria-invalid so screen readers announce it too.
 */
const FormField = ({
  as = "input",
  label,
  value,
  onChange,
  onBlur,
  error,
  show,
  required = false,
  className = "",
  ...rest
}) => {
  const id = useId();
  const visible = show && error;
  const Tag = as;

  const base =
    "w-full rounded-xl border bg-white/[0.03] px-5 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors";
  const border = visible ? "border-red-400/70 focus:border-red-400" : "border-white/10 focus:border-volt/60";

  return (
    <div className={className}>
      {/* Visible placeholder carries the label, so this stays available to assistive tech only. */}
      <label htmlFor={id} className="sr-only">
        {label}
        {required ? " (required)" : ""}
      </label>
      <Tag
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={visible ? "true" : "false"}
        aria-describedby={visible ? `${id}-error` : undefined}
        aria-required={required || undefined}
        className={`${base} ${border} ${as === "textarea" ? "resize-none" : ""}`}
        {...rest}
      />
      {visible && (
        <p id={`${id}-error`} role="alert" className="mt-2 flex items-start gap-1.5 text-xs text-red-400">
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;
